/**
 * FreshCards Kanban View — v2 with @dnd-kit
 * Replaces the broken HTML5 drag-and-drop (which was using
 * setDragImage / dataTransfer patterns that don't survive React's
 * synthetic events across the strict mode boundary).
 *
 * v2 features:
 * - @dnd-kit/core for accessible, keyboard-friendly drag-and-drop
 * - DragOverlay for visual feedback while dragging
 * - Column-level drop zones (drop on the column body, not on a card)
 * - "Add item" inline button on each column header
 * - Group-by property persists in DatabaseView state (out of scope here)
 */
import React, { useState, useMemo, useCallback } from 'react';
import {
  Paper,
  Card,
  Text,
  Group,
  ActionIcon,
  ScrollArea,
  Stack,
  Button,
  Modal,
  TextInput,
  Box,
  Flex,
  Menu,
} from '@mantine/core';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  IconGripVertical,
  IconDots,
  IconEdit,
  IconTrash,
  IconCopy,
  IconPlus,
} from '@tabler/icons-react';
import { DatabaseItem, Database, PropertyDefinition } from '../../../types/models/Database';

interface KanbanViewProps {
  database: Database;
  items: DatabaseItem[];
  groupByPropertyId?: string;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemCreate?: (item: DatabaseItem) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  onItemDelete?: (itemId: string) => void;
  onItemDuplicate?: (item: DatabaseItem) => void;
}

interface KanbanColumn {
  id: string;
  name: string;
  color: string;
  items: DatabaseItem[];
}

// V4 fragment: kanban-view
// Behavior: drag a card between columns → updates the item's group-by property value
export const KanbanView: React.FC<KanbanViewProps> = ({
  database,
  items,
  groupByPropertyId,
  onItemSelect,
  onItemCreate,
  onItemUpdate,
  onItemDelete,
  onItemDuplicate,
}) => {
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<DatabaseItem | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  // Build kanban columns from the group-by property's options.
  const columns: KanbanColumn[] = useMemo(() => {
    if (!groupByPropertyId) {
      // No group-by set: fall back to Active/Completed/Archived
      return [
        { id: 'active', name: 'Active', color: '#52b788', items },
        { id: 'completed', name: 'Completed', color: '#588b8b', items: [] },
        { id: 'archived', name: 'Archived', color: '#adb5bd', items: [] },
      ];
    }
    const property = database.schema.properties.find(p => p.id === groupByPropertyId);
    if (!property) {
      return [{ id: 'ungrouped', name: 'Ungrouped', color: '#adb5bd', items }];
    }
    const columnMap = new Map<string, { name: string; color: string; items: DatabaseItem[] }>();
    (property.options || property.statusOptions || []).forEach(opt => {
      columnMap.set(opt.id, { name: opt.name, color: opt.color, items: [] });
    });
    items.forEach(item => {
      const prop = item.properties.find(p => p.propertyId === groupByPropertyId);
      if (prop) {
        const valueKey = typeof prop.value === 'object' && prop.value !== null
          ? (prop.value as any).id
          : String(prop.value);
        const col = columnMap.get(valueKey as string);
        if (col) col.items.push(item);
      }
    });
    return Array.from(columnMap.entries()).map(([id, data]) => ({ id, ...data }));
  }, [database.schema.properties, groupByPropertyId, items]);

  // dnd-kit handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || !groupByPropertyId) return;
    const itemId = String(active.id);
    const targetColumnId = String(over.id);

    const item = items.find(i => i.id === itemId);
    if (!item) return;

    // Update the item's group-by property value
    const column = columns.find(c => c.id === targetColumnId);
    if (!column) return;

    const newValue = column.id.startsWith('status_') || column.id === 'active' || column.id === 'completed'
      ? { id: column.id, name: column.name, color: column.color, state: column.id }
      : column.id;

    const updatedItem: DatabaseItem = {
      ...item,
      properties: item.properties.map(p =>
        p.propertyId === groupByPropertyId ? { ...p, value: newValue } : p
      ),
      updatedAt: new Date(),
    };
    onItemUpdate?.(updatedItem);
  }, [items, columns, groupByPropertyId, onItemUpdate]);

  // Item-level handlers
  const handleAddItemToColumn = (columnId: string) => {
    setSelectedColumn(columnId);
    setIsAddingItem(true);
    setNewItemTitle('');
  };

  const handleCreateItem = () => {
    if (!newItemTitle.trim() || !selectedColumn) return;
    const column = columns.find(c => c.id === selectedColumn);
    if (!column) return;
    const newValue = column.id.startsWith('status_') || column.id === 'active' || column.id === 'completed'
      ? { id: column.id, name: column.name, color: column.color, state: column.id }
      : column.id;
    const newItem: DatabaseItem = {
      id: `item_${Date.now()}`,
      databaseId: database.id,
      title: newItemTitle,
      properties: groupByPropertyId
        ? [{ propertyId: groupByPropertyId, value: newValue }]
        : [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    onItemCreate?.(newItem);
    setIsAddingItem(false);
    setNewItemTitle('');
    setSelectedColumn(null);
  };

  const handleDeleteItem = (itemId: string) => {
    if (confirm('Delete this item?')) {
      onItemDelete?.(itemId);
    }
  };

  const activeItem = activeId ? items.find(i => i.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="kanban-view-wrapper" style={{ width: '100%', height: '100%' }}>
        <ScrollArea type="auto" style={{ width: '100%', height: 'calc(100vh - 180px)' }}>
          <Flex gap="md" p="md" style={{ minWidth: 'fit-content' }}>
            {columns.map(column => (
              <KanbanColumnView
                key={column.id}
                column={column}
                onAddItem={() => handleAddItemToColumn(column.id)}
                onItemSelect={onItemSelect}
                onItemEdit={setEditingItem}
                onItemDelete={handleDeleteItem}
                onItemDuplicate={onItemDuplicate}
              />
            ))}
          </Flex>
        </ScrollArea>

        <DragOverlay>
          {activeItem ? <KanbanCardPreview item={activeItem} /> : null}
        </DragOverlay>

        {/* Add Item Modal */}
        <Modal
          opened={isAddingItem}
          onClose={() => { setIsAddingItem(false); setSelectedColumn(null); }}
          title={`Add to ${columns.find(c => c.id === selectedColumn)?.name ?? 'column'}`}
          size="sm"
        >
          <Stack>
            <TextInput
              label="Title"
              placeholder="Enter item title"
              value={newItemTitle}
              onChange={e => setNewItemTitle(e.currentTarget.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreateItem(); }}
              autoFocus
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={() => { setIsAddingItem(false); setSelectedColumn(null); }}>
                Cancel
              </Button>
              <Button onClick={handleCreateItem} disabled={!newItemTitle.trim()}>
                Create
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Edit Item Modal */}
        <Modal
          opened={!!editingItem}
          onClose={() => setEditingItem(null)}
          title="Edit item"
          size="sm"
        >
          {editingItem && (
            <Stack>
              <TextInput
                label="Title"
                value={editingItem.title}
                onChange={e => setEditingItem({ ...editingItem, title: e.currentTarget.value })}
              />
              <Group justify="flex-end" gap="sm">
                <Button variant="subtle" onClick={() => setEditingItem(null)}>Cancel</Button>
                <Button onClick={() => { onItemUpdate?.(editingItem); setEditingItem(null); }}>Save</Button>
              </Group>
            </Stack>
          )}
        </Modal>
      </div>
    </DndContext>
  );
};

/** Single column — droppable zone, holds the items list. */
const KanbanColumnView: React.FC<{
  column: KanbanColumn;
  onAddItem: () => void;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemEdit: (item: DatabaseItem) => void;
  onItemDelete: (itemId: string) => void;
  onItemDuplicate?: (item: DatabaseItem) => void;
}> = ({ column, onAddItem, onItemSelect, onItemEdit, onItemDelete, onItemDuplicate }) => {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <Paper
      p="md"
      radius="md"
      ref={setNodeRef}
      style={{
        minWidth: 320,
        maxHeight: 'calc(100vh - 240px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isOver
          ? 'var(--fc-bg-secondary, #f7f6f3)'
          : 'var(--fc-bg-tertiary, #f1f1ef)',
        outline: isOver ? '2px dashed var(--fc-accent-primary, #475569)' : 'none',
        transition: 'all 150ms ease',
      }}
    >
      {/* Column header */}
      <Group justify="space-between" mb="md" align="center">
        <Group gap="sm" align="center">
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: 4,
              backgroundColor: column.color,
            }}
          />
          <Stack gap={0}>
            <Text fw={600} size="sm">{column.name}</Text>
            <Text size="xs" c="dimmed">{column.items.length} items</Text>
          </Stack>
        </Group>
      </Group>

      {/* Items */}
      <Stack
        gap="sm"
        style={{ flex: 1, minHeight: 200, overflowY: 'auto' }}
      >
        {column.items.map(item => (
          <DraggableCard
            key={item.id}
            item={item}
            onClick={() => onItemSelect?.(item)}
            onEdit={() => onItemEdit(item)}
            onDelete={() => onItemDelete(item.id)}
            onDuplicate={() => onItemDuplicate?.(item)}
          />
        ))}
        {column.items.length === 0 && (
          <Box
            style={{
              textAlign: 'center',
              padding: '20px',
              color: 'var(--fc-text-muted, #9ca3af)',
              minHeight: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed var(--fc-border-secondary, #d4d4d1)',
              borderRadius: 8,
            }}
          >
            <Text size="sm" c="dimmed">Drag cards here</Text>
          </Box>
        )}
      </Stack>

      {/* Add button */}
      <Button
        variant="subtle"
        size="sm"
        fullWidth
        mt="md"
        leftSection={<IconPlus size={14} />}
        onClick={onAddItem}
        style={{ color: 'var(--fc-text-secondary, #4b5563)' }}
      >
        Add item
      </Button>
    </Paper>
  );
};

/** Draggable card — wraps Mantine Card with dnd-kit's useDraggable.
 * The whole card is draggable (not just the grip icon) so Playwright
 * tests and touch users can drag from anywhere on the card. */
const DraggableCard: React.FC<{
  item: DatabaseItem;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}> = ({ item, onClick, onEdit, onDelete, onDuplicate }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: item.id });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
    cursor: 'grab',
    touchAction: 'none',
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      withBorder
      radius="md"
      padding="sm"
      onClick={onClick}
      {...attributes}
      {...listeners}
    >
      <Group justify="space-between" align="flex-start" mb="xs" wrap="nowrap">
        <Group gap={4} align="flex-start" style={{ flex: 1, minWidth: 0 }}>
          <ActionIcon
            size="xs"
            variant="subtle"
            color="gray"
            component="div"
            style={{ cursor: 'grab' }}
          >
            <IconGripVertical size={14} />
          </ActionIcon>
          <Text size="sm" fw={500} lineClamp={2} style={{ flex: 1 }}>
            {item.title}
          </Text>
        </Group>
        <Menu shadow="md" position="bottom-end">
          <Menu.Target>
            <ActionIcon size="xs" variant="subtle" color="gray" onClick={e => e.stopPropagation()}>
              <IconDots size={14} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconEdit size={14} />} onClick={(e) => { e.stopPropagation(); onEdit(); }}>
              Edit
            </Menu.Item>
            <Menu.Item leftSection={<IconCopy size={14} />} onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
              Duplicate
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
      <Text size="xs" c="dimmed">
        {new Date(item.createdAt).toLocaleDateString()}
      </Text>
    </Card>
  );
};

/** Drag-overlay preview — the card shown while dragging. */
const KanbanCardPreview: React.FC<{ item: DatabaseItem }> = ({ item }) => (
  <Card withBorder radius="md" padding="sm" style={{ boxShadow: 'var(--fc-shadow-lg, 0 8px 16px rgba(0,0,0,0.12))', cursor: 'grabbing', width: 280 }}>
    <Text size="sm" fw={500} lineClamp={2}>{item.title}</Text>
    <Text size="xs" c="dimmed" mt={4}>{new Date(item.createdAt).toLocaleDateString()}</Text>
  </Card>
);
